import { create } from 'zustand';

/**
 * Is this failure the network, or did the server actually answer?
 *
 * The distinction decides whether a sale goes into the offline queue or is
 * refused, so it has to be exact. Axios sets `response` only when an HTTP
 * status came back — a 4xx and a 5xx both have one. No response means the
 * request never completed a round trip: DNS, TCP, TLS, a timeout, or the
 * browser refusing to send while offline. Those are the only cases the queue
 * is for.
 *
 * A 5xx deliberately does NOT queue. The server heard the request and might
 * have written part of it; retrying that blindly from a queue is how you get a
 * sale posted from a state nobody inspected. It surfaces to the cashier as it
 * does today.
 */
export function isNetworkError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const e = err as { response?: unknown; code?: string; message?: string };
  // A cancelled request is not a network failure — nobody is waiting on it.
  if (e.code === 'ERR_CANCELED') return false;
  if (e.response != null) return false;
  // Axios sets one of these on every transport failure; `isAxiosError` is not
  // imported here to keep this module dependency-free and testable.
  return (
    e.code === 'ERR_NETWORK' ||
    e.code === 'ECONNABORTED' ||
    e.code === 'ETIMEDOUT' ||
    e.code === 'ERR_INTERNET_DISCONNECTED' ||
    typeof e.message === 'string'
  );
}

interface NetworkState {
  /**
   * Best guess at whether the API is reachable.
   *
   * Seeded from `navigator.onLine`, which is a hint and not much more — it
   * reports a link, not a route, and a captive portal or a dead uplink still
   * reads as online. So a failed charge marks us offline directly, and a
   * successful call marks us back on. The browser events only nudge it.
   */
  online: boolean;
  setOnline: (online: boolean) => void;
}

export const useNetwork = create<NetworkState>((set) => ({
  online: typeof navigator === 'undefined' ? true : navigator.onLine !== false,
  setOnline: (online) =>
    set((s) => (s.online === online ? s : { online })),
}));

/** Call once at boot. Returns a teardown for symmetry; the app never unmounts. */
export function watchNetwork(): () => void {
  if (typeof window === 'undefined') return () => {};
  const up = () => useNetwork.getState().setOnline(true);
  const down = () => useNetwork.getState().setOnline(false);
  window.addEventListener('online', up);
  window.addEventListener('offline', down);
  return () => {
    window.removeEventListener('online', up);
    window.removeEventListener('offline', down);
  };
}
